import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import API_BASE_URL from '../config/baseUrl';
import { toast } from 'react-toastify';

const SetMatkaResultPage = () => {
  const navigate = useNavigate();
  const { accessToken, user } = useSelector((state) => state.auth);
  const getTodayDate = () => {
    const today = new Date();
    return (
      today.getFullYear() +
      '-' +
      String(today.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(today.getDate()).padStart(2, '0')
    );
  };

  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState('');
  const [selectedMarket, setSelectedMarket] = useState('');
  const [result, setResult] = useState('');
  const [resultDate, setResultDate] = useState(getTodayDate);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmNumbers, setConfirmNumbers] = useState([]);

  // Market types
  const marketTypes = [
    { value: 'JODI', label: 'Jodi', min: 0, max: 99 },
    { value: 'HARUP_ANDAR', label: 'Harup Andar', min: 0, max: 9 },
    { value: 'HARUP_BAHAR', label: 'Harup Bahar', min: 0, max: 9 },
  ];

  // Check if user is Super Admin
  useEffect(() => {
    if (user?.userTypeInfo !== 7) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch matches on component mount
  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/matka/list`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.status) {
        setMatches(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to fetch matches');
      }
    } catch (err) {
      console.error('Error fetching matches:', err);
      setError('Failed to load matches. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMatchChange = (e) => {
    setSelectedMatch(e.target.value);
    setSelectedMarket('');
    setResult('');
  };

  const handleMarketChange = (e) => {
    setSelectedMarket(e.target.value);
    setResult('');
  };

  const handleResultChange = (e) => {
    const value = e.target.value;
    const selectedMarketType = marketTypes.find(m => m.value === selectedMarket);
    
    if (!selectedMarketType) return;

    // Only allow numbers within the valid range
    if (value === '' || (parseInt(value) >= selectedMarketType.min && parseInt(value) <= selectedMarketType.max)) {
      setResult(value);
    }
  };

  const generateConfirmNumbers = (originalNumber, min, max) => {
    const numbers = [originalNumber];
    const offsets = [2, 4, -3, -1, 3]; // Different offsets to create variety
    
    offsets.forEach(offset => {
      let newNum = originalNumber + offset;
      
      // Ensure number is within range
      if (newNum < min) newNum = min + Math.abs(offset);
      if (newNum > max) newNum = max - Math.abs(offset);
      
      // Ensure no duplicates
      if (!numbers.includes(newNum) && newNum >= min && newNum <= max) {
        numbers.push(newNum);
      }
    });

    // Fill remaining slots if needed
    while (numbers.length < 6) {
      const randomOffset = Math.floor(Math.random() * 10) - 5;
      let newNum = originalNumber + randomOffset;
      
      if (newNum < min) newNum = min;
      if (newNum > max) newNum = max;
      
      if (!numbers.includes(newNum)) {
        numbers.push(newNum);
      }
    }

    // Shuffle the array so original number is not always in the same position
    return numbers.sort(() => Math.random() - 0.5).slice(0, 6);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedMatch || !selectedMarket || result === '' || !resultDate) {
      setError('Please fill in all fields');
      return;
    }

    const selectedMarketType = marketTypes.find(m => m.value === selectedMarket);
    const resultNum = parseInt(result);

    // Generate confirmation numbers
    const confirmNums = generateConfirmNumbers(resultNum, selectedMarketType.min, selectedMarketType.max);
    setConfirmNumbers(confirmNums);
    setShowConfirmModal(true);
  };

  const setMatkaResult = async (payload) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/matka/set-matka-result`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.data?.status) {
        throw new Error(response.data?.message || 'Failed to set matka result');
      }

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to set matka result';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmNumber = async (selectedNum) => {
    const resultNum = parseInt(result);

    if (selectedNum === resultNum) {
      const payload = {
        matkaId: String(selectedMatch),
        marketId: selectedMarket.replace(/^HARUP_/, ''),
        result: String(resultNum),
        resultDate: resultDate
      };

      const apiResult = await setMatkaResult(payload);
      if (apiResult.success) {
        toast.success('Result saved successfully!');
        setSelectedMatch('');
        setSelectedMarket('');
        setResult('');
        setResultDate(getTodayDate());
        setShowConfirmModal(false);
        setConfirmNumbers([]);
      } else {
        toast.error(apiResult.error || 'Failed to save result');
        setShowConfirmModal(false);
        setConfirmNumbers([]);
      }
    } else {
      // Wrong number selected
      toast.error('You selected the wrong number. Please try again.');
      setShowConfirmModal(false);
      setConfirmNumbers([]);
    }
  };

  const handleBack = () => {
    navigate('/sports-betting/matka');
  };

  const selectedMarketType = marketTypes.find(m => m.value === selectedMarket);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold capitalize">Set Matka Result</h1>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded shadow">
        <form onSubmit={handleSubmit}>
          {/* Card Header */}
          <div className="bg-[#6f42c1] p-4">
            <div className="flex justify-between items-center flex-wrap gap-4 mb-4">
              <div className="text-white text-lg capitalize font-medium">Set Matka Result</div>
              <button
                type="button"
                onClick={handleBack}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
              >
                Back
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-500 text-white rounded">
                {error}
              </div>
            )}

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Select Match Type */}
              <div>
                <label className="block text-white text-sm mb-2">Select Match Type</label>
                <select
                  required
                  value={selectedMatch}
                  onChange={handleMatchChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                >
                  <option value="">Select Market</option>
                  {matches.map((match) => (
                    <option key={match.id} value={match.id}>
                      {match.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Select Market Type */}
              <div>
                <label className="block text-white text-sm mb-2">Select Market Type</label>
                <select
                  required
                  value={selectedMarket}
                  onChange={handleMarketChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-200 disabled:cursor-not-allowed"
                  disabled={!selectedMatch}
                >
                  <option value="">Select Market Type</option>
                  {marketTypes.map((market) => (
                    <option key={market.value} value={market.value}>
                      {market.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Result Input */}
              <div>
                <label className="block text-white text-sm mb-2">Result</label>
                <input
                  required
                  type="number"
                  placeholder="Enter Result"
                  value={result}
                  onChange={handleResultChange}
                  min={selectedMarketType?.min || 0}
                  max={selectedMarketType?.max || 99}
                  className="w-full px-3 py-2 border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-200 disabled:cursor-not-allowed"
                  disabled={!selectedMarket}
                />
              </div>

              {/* Result Date */}
              <div>
                <label className="block text-white text-sm mb-2">Result Date</label>
                <input
                  required
                  type="date"
                  value={resultDate}
                  onChange={(e) => setResultDate(e.target.value)}
                  max={getTodayDate()}
                  className="w-full px-3 py-2 border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-200 disabled:cursor-not-allowed"
                  disabled={!selectedMarket}
                />
              </div>

              {/* Submit Button */}
              <div className="flex items-end">
                <button
                  type="submit"
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed"
                  disabled={!selectedMatch || !selectedMarket || result === '' || !resultDate || loading}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <ConfirmationModal
          numbers={confirmNumbers}
          onSelect={handleConfirmNumber}
          onClose={() => {
            setShowConfirmModal(false);
            setConfirmNumbers([]);
          }}
        />
      )}
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ numbers, onSelect, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Modal Content */}
          <h3 className="text-xl font-semibold mb-4 text-gray-900">
            Confirm Your Number
          </h3>
          <p className="text-gray-600 mb-6">
            Please click on the number you entered to confirm:
          </p>

          {/* Number Grid */}
          <div className="grid grid-cols-3 gap-4">
            {numbers.map((num, index) => (
              <button
                key={index}
                onClick={() => onSelect(num)}
                className="bg-blue-500 hover:bg-blue-600 text-white text-2xl font-bold py-6 rounded-lg transition-colors transform hover:scale-105"
              >
                {String(num).padStart(2, '0')}
              </button>
            ))}
          </div>

          <p className="text-sm text-gray-500 mt-4 text-center">
            Click on the correct number to confirm the result
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetMatkaResultPage;
